
import { MelodyName, BallSoundEffect, SimulationConfig, MelodyInstrument, MelodyData, RhythmicNote } from './types';
import { NOTES, MELODIES_RAW } from './audioData';

const MELODIES: Record<string, MelodyData> = {};
Object.entries(MELODIES_RAW).forEach(([name, notes]) => {
  // Cast to MelodyData to allow mix of numbers and objects if needed later
  MELODIES[name] = notes as MelodyData;
});


interface StudioRack {
  reverbNode: ConvolverNode;
  reverbGain: GainNode;
  delayL: DelayNode;
  delayR: DelayNode;
  feedbackL: GainNode;
  feedbackR: GainNode;
  wetGain: GainNode;
  channelSplitter: ChannelSplitterNode;
  channelMerger: ChannelMergerNode;
  filterNode: BiquadFilterNode;
  distortionNode: WaveShaperNode;
}

export class AudioService {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private noteIndex: number = 0;
  private bufferCache: Record<string, AudioBuffer> = {};
  private noiseBuffer: AudioBuffer | null = null;
  
  // Dual Racks
  private melodyRack: StudioRack | null = null;
  private fxRack: StudioRack | null = null;
  
  private activeVoices: number = 0;
  private readonly MAX_VOICES = 64; 
  private lastSlidingTick: number = 0;

  // SONG SLICE STATE
  private songBuffer: AudioBuffer | null = null;
  private songCursor: number = 0;
  private lastSongSource: AudioBufferSourceNode | null = null;
  private lastSongGain: GainNode | null = null;
  private lastSliceTriggerTime: number = 0;
  private lastSliceScheduledEndTime: number = 0;

  constructor() {
    this.ctx = null;
  }

  public resume() {
    this.initCtx();
  }

  private createRack(ctx: AudioContext): StudioRack {
    const reverbNode = ctx.createConvolver();
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0;

    const delayL = ctx.createDelay(2.0);
    const delayR = ctx.createDelay(2.0);
    const feedbackL = ctx.createGain();
    const feedbackR = ctx.createGain();
    const wetGain = ctx.createGain();
    const channelSplitter = ctx.createChannelSplitter(2);
    const channelMerger = ctx.createChannelMerger(2);

    const filterNode = ctx.createBiquadFilter();
    filterNode.type = 'lowpass';
    filterNode.frequency.value = 20000;
    filterNode.Q.value = 1.0;

    const distortionNode = ctx.createWaveShaper();
    distortionNode.curve = this.makeDistortionCurve(0);
    (distortionNode as any)._lastAmt = 0;

    // Chain
    distortionNode.connect(filterNode);
    filterNode.connect(this.masterGain!);

    // Reverb Parallel
    filterNode.connect(reverbNode);
    reverbNode.connect(reverbGain);
    reverbGain.connect(this.masterGain!);

    // Stereo Echo Parallel
    filterNode.connect(channelSplitter);
    channelSplitter.connect(delayL, 0);
    delayL.connect(feedbackL);
    feedbackL.connect(delayR); 
    delayL.connect(channelMerger, 0, 0);

    channelSplitter.connect(delayR, 1);
    delayR.connect(feedbackR);
    feedbackR.connect(delayL);
    delayR.connect(channelMerger, 0, 1);

    channelMerger.connect(wetGain);
    wetGain.connect(this.masterGain!);

    return { 
      reverbNode, reverbGain, delayL, delayR, feedbackL, feedbackR, 
      wetGain, channelSplitter, channelMerger, filterNode, distortionNode 
    };
  }

  private initCtx() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 'interactive' });
      this.masterGain = this.ctx.createGain();
      this.limiter = this.ctx.createDynamicsCompressor();
      this.limiter.threshold.setValueAtTime(-3, this.ctx.currentTime); // Standard limit
      this.limiter.ratio.setValueAtTime(12, this.ctx.currentTime);
      this.limiter.attack.setValueAtTime(0.002, this.ctx.currentTime);
      this.limiter.release.setValueAtTime(0.25, this.ctx.currentTime);
      this.limiter.connect(this.ctx.destination);
      this.masterGain.connect(this.limiter);

      this.melodyRack = this.createRack(this.ctx);
      this.fxRack = this.createRack(this.ctx);
      this.setupReverbBuffers();
      this.initNoiseBuffer(this.ctx);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  private initNoiseBuffer(ctx: AudioContext) {
      const bufferSize = ctx.sampleRate * 2;
      this.noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const out = this.noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) out[i] = Math.random() * 2 - 1;
  }

  private makeDistortionCurve(amount: number) {
    const k = Math.max(0, amount);
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }

  private setupReverbBuffers() {
    if (!this.ctx || !this.melodyRack || !this.fxRack) return;
    const len = this.ctx.sampleRate * 2.5; 
    const buf = this.ctx.createBuffer(2, len, this.ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 4);
    }
    this.melodyRack.reverbNode.buffer = buf;
    this.fxRack.reverbNode.buffer = buf;
  }

  private updateRack(rack: StudioRack, config: SimulationConfig, isMelody: boolean) {
    if (!this.ctx || !this.masterGain) return;
    const now = this.ctx.currentTime;
    
    // Update Master Volume
    this.masterGain.gain.setTargetAtTime(config.masterVolume, now, 0.05);
    
    const reverb = isMelody ? config.audioReverbLevel : config.fxReverbLevel;
    const dist = isMelody ? config.audioDistortionLevel : config.fxDistortionLevel;
    const lpf = isMelody ? config.audioLowPassFreq : config.fxLowPassFreq;
    const q = isMelody ? config.audioLowPassResonance : config.fxLowPassResonance;
    const delayEnable = isMelody ? config.audioEcho : config.fxEcho;
    const delayTime = isMelody ? config.audioDelayTime : config.fxDelayTime;
    const delayFb = isMelody ? config.audioDelayFeedback : config.fxDelayFeedback;
    const wet = isMelody ? config.audioEchoWet : config.fxEchoWet;

    rack.reverbGain.gain.setTargetAtTime(reverb, now, 0.05);
    rack.filterNode.frequency.setTargetAtTime(lpf, now, 0.05);
    rack.filterNode.Q.setTargetAtTime(q, now, 0.05);
    
    rack.delayL.delayTime.setTargetAtTime(Math.max(0.01, delayTime), now, 0.05);
    rack.delayR.delayTime.setTargetAtTime(Math.max(0.01, delayTime * 1.01), now, 0.05);
    
    const fb = delayEnable ? delayFb : 0;
    rack.feedbackL.gain.setTargetAtTime(fb, now, 0.05);
    rack.feedbackR.gain.setTargetAtTime(fb, now, 0.05);
    rack.wetGain.gain.setTargetAtTime(delayEnable ? wet : 0, now, 0.05);

    const lastAmt = (rack.distortionNode as any)._lastAmt ?? -1;
    if (Math.abs(dist - lastAmt) > 0.5) {
      rack.distortionNode.curve = this.makeDistortionCurve(dist * 2);
      (rack.distortionNode as any)._lastAmt = dist;
    }
  }

  public resetIndex() { this.noteIndex = 0; }
  
  public resetSongCursor() { this.songCursor = 0; }

  // UPLOAD FOR SLICING
  public async uploadSongForSlicing(file: File) {
      const ctx = this.initCtx();
      const ab = await file.arrayBuffer();
      try {
          this.songBuffer = await ctx.decodeAudioData(ab);
          this.songCursor = 0;
          return true;
      } catch (e) {
          console.error("Failed to decode song for slicing", e);
          return false;
      }
  }

  public playNextSlice(config: SimulationConfig) {
      if (!this.songBuffer) return;
      const ctx = this.initCtx();
      const now = ctx.currentTime;
      
      // Prevent extreme spamming from crashing or sounding like noise
      if (now - this.lastSliceTriggerTime < 0.01) return;

      const duration = Math.max(0.05, config.songSliceDuration || 0.15);
      
      // "At the max, playing like a normal song" logic:
      // If we are hitting faster than the slice duration, only advance the cursor by the time elapsed.
      // This ensures the song plays at 1x speed even if hits are 100fps.
      if (this.lastSliceTriggerTime > 0) {
          const dt = now - this.lastSliceTriggerTime;
          // If we are hitting fast, we only advance by the time passed to keep it "normal speed"
          // If we are hitting slow, we advance by at least the duration to keep it "slicy"
          const advance = Math.min(duration, dt);
          this.lastSliceTriggerTime = now;
          this.songCursor += advance;
      } else {
          this.lastSliceTriggerTime = now;
      }

      // Handle Loop
      if (this.songCursor >= this.songBuffer.duration) {
          this.songCursor = 0;
      }
      
      const startTime = this.songCursor;
      
      // NO OVERLAP LOGIC:
      // If a previous slice is still playing, fade it out immediately to avoid mud
      if (this.lastSongGain) {
          this.lastSongGain.gain.cancelScheduledValues(now);
          this.lastSongGain.gain.setTargetAtTime(0, now, 0.01);
          if (this.lastSongSource) {
              try { this.lastSongSource.stop(now + 0.05); } catch(e) {}
          }
      }

      const source = ctx.createBufferSource();
      source.buffer = this.songBuffer;
      const gain = ctx.createGain();
      
      this.lastSongSource = source;
      this.lastSongGain = gain;
      this.lastSliceScheduledEndTime = now + duration;

      this.activeVoices++;
      
      // Connect to Rack
      this.updateRack(this.melodyRack!, config, true);
      source.connect(gain);
      gain.connect(this.melodyRack!.distortionNode);
      
      // Smooth Envelope
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(config.melodyVolume, now + 0.01);
      gain.gain.setValueAtTime(config.melodyVolume, now + duration - 0.01);
      gain.gain.linearRampToValueAtTime(0, now + duration);
      
      source.start(now, startTime, duration);
      source.stop(now + duration + 0.1);
      
      setTimeout(() => {
          this.activeVoices = Math.max(0, this.activeVoices - 1);
      }, duration * 1000 + 100);
  }

  public previewSlice(config: SimulationConfig) {
      this.playNextSlice(config);
  }

  public playSlidingSound(velocity: number, config: SimulationConfig) {
    if (!config.audioSlidingEnabled) return;
    const ctx = this.initCtx();
    const now = ctx.currentTime;
    const speed = Math.min(velocity / 10, 1.0);
    const interval = (1.0 / config.audioSlidingMaxSpeed) / Math.max(speed, 0.1);
    
    if (now - this.lastSlidingTick < interval) return;
    this.lastSlidingTick = now;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    const freq = (200 + speed * 1500) * config.audioSlidingPitch;
    osc.frequency.setValueAtTime(freq, now);
    
    const vol = config.audioSlidingVolume * config.melodyVolume;
    gain.gain.setValueAtTime(vol, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
    
    osc.connect(gain);
    this.updateRack(this.melodyRack!, config, true);
    gain.connect(this.melodyRack!.distortionNode);
    osc.start(now); osc.stop(now + 0.05);
  }

  public async playNote(melodyName: MelodyName, config: SimulationConfig, baseVolume: number = 1.0, force: boolean = false) {
    if (!force) {
        if (this.activeVoices >= this.MAX_VOICES) return;
    }
    this.executePlayNote(melodyName, config, baseVolume, force);
  }

  // Helper to get duration of the LAST played note (for physics sync)
  public getLastNoteDuration(melodyName: MelodyName, config: SimulationConfig): number {
      const melody = MELODIES[melodyName] || (melodyName === 'Custom (Uploaded)' ? config.customFrequencies : config.customMelodies.find(m => m.name === melodyName)?.frequencies);
      if (!melody || melody.length === 0) return 300; // Default fallback duration
      
      // noteIndex was incremented in playNote, so (index - 1) is what just played.
      const idx = (this.noteIndex - 1 + melody.length) % melody.length;
      const noteData = melody[idx];
      
      if (typeof noteData === 'object' && !Array.isArray(noteData) && 'duration' in noteData) {
          return (noteData as RhythmicNote).duration || 300;
      }
      return 300;
  }

  private synthTone(ctx: AudioContext, freq: number, startTime: number, instrument: MelodyInstrument, vol: number, dest: AudioNode, trail: boolean) {
      const dur = trail ? 2.5 : 0.8;
      
      if (instrument === 'GRAND_PIANO') {
          // 3 Oscillators for fullness
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const osc3 = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc1.type = 'sine'; osc1.frequency.value = freq;
          osc2.type = 'triangle'; osc2.frequency.value = freq;
          osc3.type = 'sine'; osc3.frequency.value = freq / 2; // Sub-octave
          
          const master = ctx.createGain();
          master.gain.setValueAtTime(0, startTime);
          master.gain.linearRampToValueAtTime(vol * 0.8, startTime + 0.01); // Fast attack
          master.gain.exponentialRampToValueAtTime(vol * 0.6, startTime + 0.1); // Decay to sustain
          master.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          
          osc1.connect(master);
          osc2.connect(master); // Triangle gives body
          osc3.connect(master);
          master.connect(dest);
          
          osc1.start(startTime); osc2.start(startTime); osc3.start(startTime);
          osc1.stop(startTime + dur); osc2.stop(startTime + dur); osc3.stop(startTime + dur);
      } 
      else if (instrument === 'ELECTRIC_GUITAR') {
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.value = freq;
          
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(freq * 4, startTime);
          filter.frequency.exponentialRampToValueAtTime(freq, startTime + 0.3); // Pluck filter envelope
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(dest);
          
          osc.start(startTime); osc.stop(startTime + dur);
      }
      else if (instrument === 'FLUTE') {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = freq;
          
          const noise = ctx.createBufferSource();
          noise.buffer = this.noiseBuffer;
          noise.loop = true;
          
          const noiseGain = ctx.createGain();
          noiseGain.gain.value = 0.05 * vol;
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.1); // Soft attack
          gain.gain.linearRampToValueAtTime(vol * 0.8, startTime + 0.3);
          gain.gain.linearRampToValueAtTime(0, startTime + dur);
          
          osc.connect(gain);
          noise.connect(noiseGain);
          noiseGain.connect(gain);
          gain.connect(dest);
          
          osc.start(startTime); noise.start(startTime);
          osc.stop(startTime + dur); noise.stop(startTime + dur);
      }
      else if (instrument === 'SYNTH_PLUCK') {
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.value = freq;
          
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.Q.value = 5;
          filter.frequency.setValueAtTime(freq * 8, startTime);
          filter.frequency.exponentialRampToValueAtTime(freq, startTime + 0.2);
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
          
          osc.connect(filter); filter.connect(gain); gain.connect(dest);
          osc.start(startTime); osc.stop(startTime + 0.7);
      }
      else if (instrument === '8BIT_LEAD') {
          const osc = ctx.createOscillator();
          osc.type = 'square';
          osc.frequency.value = freq;
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(vol, startTime);
          gain.gain.setValueAtTime(vol, startTime + 0.1);
          gain.gain.linearRampToValueAtTime(0, startTime + 0.2); // Short blip
          
          osc.connect(gain); gain.connect(dest);
          osc.start(startTime); osc.stop(startTime + 0.25);
      }
      else if (instrument === 'ETHEREAL_CHIME') {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = freq;
          
          const osc2 = ctx.createOscillator();
          osc2.type = 'sine';
          osc2.frequency.value = freq * 2.5; // Harmonic
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + 3.0); // Long tail
          
          osc.connect(gain); osc2.connect(gain); gain.connect(dest);
          osc.start(startTime); osc2.start(startTime);
          osc.stop(startTime + 3.5); osc2.stop(startTime + 3.5);
      }
      else if (instrument === 'ORCHESTRA_STRINGS') {
          const osc = ctx.createOscillator();
          osc.type = 'sawtooth';
          osc.frequency.value = freq;
          const osc2 = ctx.createOscillator();
          osc2.type = 'sawtooth';
          osc2.frequency.value = freq * 1.01; // Detune
          
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.value = 2000;
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol * 0.7, startTime + 0.5); // Slow attack
          gain.gain.linearRampToValueAtTime(0, startTime + 2.0);
          
          osc.connect(filter); osc2.connect(filter);
          filter.connect(gain); gain.connect(dest);
          
          osc.start(startTime); osc2.start(startTime);
          osc.stop(startTime + 2.5); osc2.stop(startTime + 2.5);
      }
      else if (instrument === 'MARIMBA') {
          const osc = ctx.createOscillator();
          osc.type = 'triangle';
          osc.frequency.value = freq;
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.005);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);
          
          osc.connect(gain); gain.connect(dest);
          osc.start(startTime); osc.stop(startTime + 0.5);
      }
      else {
          // PURE_SINE or Fallback
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = freq;
          
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(vol, startTime + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + dur);
          
          osc.connect(gain); gain.connect(dest);
          osc.start(startTime); osc.stop(startTime + dur);
      }
  }

  private executePlayNote(melodyName: MelodyName, config: SimulationConfig, baseVolume: number, force: boolean) {
    const ctx = this.initCtx();
    const now = ctx.currentTime;
    
    let melody = MELODIES[melodyName] || (melodyName === 'Custom (Uploaded)' ? config.customFrequencies : config.customMelodies.find(m => m.name === melodyName)?.frequencies);
    
    if (!melody || melody.length === 0) return;

    const rawData = melody[this.noteIndex % melody.length];
    
    // Extract actual frequency data
    let noteData: number | number[] = 0;
    if (typeof rawData === 'object' && !Array.isArray(rawData) && 'notes' in rawData) {
        noteData = (rawData as RhythmicNote).notes;
    } else {
        noteData = rawData as (number | number[]);
    }

    this.noteIndex++; 
    
    const instr = config.melodyInstrument || 'GRAND_PIANO';
    const trail = config.audioTrailEffect;

    const playFreq = (f: number, delayOffset: number = 0) => {
        const finalFreq = f * Math.pow(2, config.audioPitchShift / 12);
        this.activeVoices++;
        
        // Prepare main rack node to connect to
        this.updateRack(this.melodyRack!, config, true);
        const dest = this.melodyRack!.distortionNode;
        
        // Volume calc
        const vol = baseVolume * config.melodyVolume;
        
        this.synthTone(ctx, finalFreq, now + delayOffset, instr, vol, dest, trail);
        
        const dur = trail ? 3.0 : 1.0;
        setTimeout(() => { this.activeVoices = Math.max(0, this.activeVoices - 1); }, dur * 1000);
    };

    if (Array.isArray(noteData)) {
        // Chord: Play all notes simultaneously (or slightly arpeggiated if desired)
        noteData.forEach((n, i) => {
            if (typeof n === 'number' && isFinite(n) && n > 0) {
                // Slight strumming effect for guitars
                const strum = instr === 'ELECTRIC_GUITAR' ? i * 0.03 : 0;
                playFreq(n, strum);
            }
        });
    } else if (typeof noteData === 'number' && isFinite(noteData) && noteData > 0) {
        playFreq(noteData);
    }
  }

  public async playSoundEffect(effect: BallSoundEffect, config: SimulationConfig, baseVolume: number = 1.0, force: boolean = false) {
    const ctx = this.initCtx();
    const now = ctx.currentTime;
    
    // COMPOUND MODE: Allow heavier polyphony
    const maxVoices = config.audioCompoundMode ? 96 : 32;
    if (!force && this.activeVoices >= maxVoices) return;

    this.activeVoices++;
    
    const rack = this.fxRack!;
    this.updateRack(rack, config, false);
    const dest = rack.distortionNode;
    
    // Volume Handling
    const vol = baseVolume * config.fxVolume * (config.audioCompoundMode ? 0.9 : 1.0); // Slight ducking if compound heavy

    // Helper functions for synthesis
    const envelope = (node: OscillatorNode | AudioBufferSourceNode, gain: GainNode, atk: number, dcy: number) => {
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(vol, now + atk);
        gain.gain.exponentialRampToValueAtTime(0.001, now + atk + dcy);
        node.start(now);
        node.stop(now + atk + dcy + 0.1);
        setTimeout(() => { this.activeVoices = Math.max(0, this.activeVoices - 1); }, (atk + dcy) * 1000 + 200);
    };

    const playOsc = (type: OscillatorType, freqStart: number, freqEnd: number, atk: number, dcy: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freqStart, now);
        if (freqStart !== freqEnd) osc.frequency.exponentialRampToValueAtTime(Math.max(0.1, freqEnd), now + atk + dcy);
        osc.connect(gain);
        gain.connect(dest);
        envelope(osc, gain, atk, dcy);
    };

    const playNoise = (filterType: BiquadFilterType, freq: number, atk: number, dcy: number) => {
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        src.loop = true;
        const filter = ctx.createBiquadFilter();
        filter.type = filterType;
        filter.frequency.setValueAtTime(freq, now);
        const gain = ctx.createGain();
        src.connect(filter);
        filter.connect(gain);
        gain.connect(dest);
        envelope(src, gain, atk, dcy);
    };

    switch (effect) {
      case 'Pop': playOsc('sine', 800, 100, 0.001, 0.1); break;
      case 'Wood': playOsc('sine', 400, 100, 0.001, 0.05); break;
      case 'Metal': {
          // FM Synthesis for metallic
          const osc = ctx.createOscillator();
          const mod = ctx.createOscillator();
          const gain = ctx.createGain();
          const modGain = ctx.createGain();
          osc.type = 'triangle'; osc.frequency.setValueAtTime(600, now);
          mod.type = 'square'; mod.frequency.setValueAtTime(840, now);
          modGain.gain.setValueAtTime(1000, now);
          mod.connect(modGain); modGain.connect(osc.frequency);
          osc.connect(gain); gain.connect(dest);
          mod.start(now);
          envelope(osc, gain, 0.001, 0.15);
          mod.stop(now + 0.2);
          break;
      }
      case 'Synth-Zap': playOsc('sawtooth', 1200, 200, 0.001, 0.2); break;
      case 'Crystal': playOsc('sine', 1500, 1500, 0.01, 0.8); break;
      case 'Soft-Thud': playOsc('sine', 120, 50, 0.005, 0.1); break;
      case 'Explosion': this.playExplosion(ctx, now, vol, config); this.activeVoices--; break; // Defer
      case 'Laser-Gun': playOsc('sawtooth', 800, 100, 0.001, 0.2); break;
      case 'Pistol': playNoise('lowpass', 2000, 0.001, 0.1); break;
      case 'Sniper': playNoise('highpass', 1000, 0.001, 0.05); playOsc('sine', 2000, 100, 0.001, 0.05); break;
      case 'Glass-Break': playNoise('highpass', 3000, 0.001, 0.2); playOsc('sine', 4000, 3000, 0, 0.1); break;
      case 'Digital-Beep': playOsc('square', 880, 880, 0.001, 0.08); break;
      case 'Retro-Jump': playOsc('square', 200, 600, 0.01, 0.3); break;
      case 'Thunder': playNoise('lowpass', 400, 0.1, 1.5); break;
      case 'Bubble': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(300, now);
          osc.frequency.linearRampToValueAtTime(600, now + 0.1);
          osc.frequency.linearRampToValueAtTime(300, now + 0.15);
          osc.connect(gain); gain.connect(dest);
          envelope(osc, gain, 0.01, 0.15);
          break;
      }
      case 'Camera': playNoise('bandpass', 2000, 0.001, 0.03); playOsc('sine', 3000, 3000, 0.05, 0.05); break;
      case 'Sword': playNoise('bandpass', 1500, 0.01, 0.3); playOsc('sawtooth', 2000, 4000, 0.01, 0.2); break;
      case 'Robot': {
          const osc = ctx.createOscillator();
          const lfo = ctx.createOscillator();
          const gain = ctx.createGain();
          const lfoGain = ctx.createGain();
          osc.type = 'sawtooth'; osc.frequency.setValueAtTime(200, now);
          lfo.type = 'square'; lfo.frequency.setValueAtTime(50, now);
          lfoGain.gain.value = 500;
          lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
          osc.connect(gain); gain.connect(dest);
          lfo.start(now);
          envelope(osc, gain, 0.01, 0.2);
          lfo.stop(now + 0.3);
          break;
      }
      case 'Alien': playOsc('sine', 800, 1200, 0.05, 0.4); break; // Modulated in future?
      case 'Bass-Drop': playOsc('sine', 120, 30, 0.01, 0.8); break;
      case 'Cowbell': playOsc('square', 800, 800, 0.001, 0.1); break; // Simplified
      case 'Snare': playNoise('highpass', 1000, 0.001, 0.15); playOsc('triangle', 200, 100, 0.001, 0.1); break;
      case 'Kick': playOsc('sine', 150, 50, 0.001, 0.3); break;
      case 'Hi-Hat': playNoise('highpass', 5000, 0.001, 0.05); break;
      case 'Cyber-Pulse': playOsc('sawtooth', 100, 100, 0.01, 0.3); break;
      case 'Magic': playOsc('sine', 800, 800, 0.01, 0.5); playOsc('sine', 1200, 1200, 0.05, 0.5); break;
      case 'Sparkle': playOsc('sine', 2000, 3000, 0.001, 0.1); playOsc('sine', 2500, 4000, 0.02, 0.1); break;
      case 'Steam': playNoise('lowpass', 1000, 0.1, 0.4); break;
      case 'Electric': playOsc('sawtooth', 50, 50, 0.01, 0.2); break; // Buzz
      case 'Glitch': playOsc('square', 100, 1000, 0.001, 0.05); break;
      case 'Heavy-Slam': playOsc('sine', 60, 20, 0.001, 0.4); playNoise('lowpass', 200, 0.001, 0.2); break;
      case 'Echo-Pluck': playOsc('triangle', 440, 440, 0.001, 0.1); break; // Delay handles echo
      case 'Sub-Boom': playOsc('sine', 50, 30, 0.01, 0.6); break;
      case 'Whip': playNoise('bandpass', 1000, 0.001, 0.15); break;
      case 'SciFi-Door': playNoise('lowpass', 500, 0.1, 0.5); break;
      case 'Wood-Block': playOsc('sine', 800, 800, 0.001, 0.03); break;
      case 'Marimba': playOsc('triangle', 400, 400, 0.005, 0.2); break;
      default: playOsc('sine', 440, 440, 0.01, 0.1);
    }
  }

  private playExplosion(ctx: AudioContext, time: number, vol: number, config: SimulationConfig) {
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const gain = ctx.createGain();
    
    // Add Lowpass filter sweep for heavy explosion feel
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, time);
    filter.frequency.exponentialRampToValueAtTime(100, time + 0.5);
    
    gain.gain.setValueAtTime(vol * config.fxVolume, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.8);
    
    src.connect(filter);
    filter.connect(gain);
    
    const rack = this.fxRack!; 
    this.updateRack(rack, config, false);
    gain.connect(rack.distortionNode);
    
    src.start(time); src.stop(time + 1.0);
    setTimeout(() => { this.activeVoices = Math.max(0, this.activeVoices - 1); }, 1100);
  }

  public async playCustomBuffer(source: string, volume: number = 1.0, config: SimulationConfig, isBreak: boolean = false, force: boolean = false) {
    if (!source) return;
    const ctx = this.initCtx();
    
    const maxVoices = config.audioCompoundMode ? 64 : 32;
    if (!force && this.activeVoices >= maxVoices) return;

    let buf = this.bufferCache[source];
    if (!buf) {
      try {
        let arrayBuffer: ArrayBuffer;
        if (source.startsWith('http') || source.startsWith('/')) {
            const resp = await fetch(source);
            arrayBuffer = await resp.arrayBuffer();
        } else {
            const bin = atob(source.split(',')[1] || source);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            arrayBuffer = bytes.buffer;
        }
        buf = await ctx.decodeAudioData(arrayBuffer);
        this.bufferCache[source] = buf;
      } catch (e) {
        console.error("Audio decode failed", e);
        return;
      }
    }

    this.activeVoices++;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    
    // Slight randomization for heavy stacking
    const pbRate = config.audioCompoundMode ? 0.95 + Math.random() * 0.1 : 1.0;
    src.playbackRate.value = pbRate;

    gain.gain.setValueAtTime(volume * config.fxVolume, ctx.currentTime);
    src.connect(gain);
    const rack = isBreak ? this.fxRack! : this.melodyRack!;
    this.updateRack(rack, config, !isBreak);
    gain.connect(rack.distortionNode);
    src.start();
    src.onended = () => {
        this.activeVoices = Math.max(0, this.activeVoices - 1);
    };
  }

  public async playFullSequence(melody: MelodyData, speed: number = 0.2) {
    const ctx = this.initCtx();
    melody.forEach((noteData, i) => {
      const play = (f: number) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.setValueAtTime(f, ctx.currentTime + i * speed);
          gain.gain.setValueAtTime(0.2, ctx.currentTime + i * speed);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * speed + 0.4);
          osc.connect(gain);
          gain.connect(this.masterGain!);
          osc.start(ctx.currentTime + i * speed); osc.stop(ctx.currentTime + i * speed + 0.5);
      };
      
      let finalNotes: number[] = [];
      if (typeof noteData === 'object' && !Array.isArray(noteData) && 'notes' in noteData) {
          const n = (noteData as RhythmicNote).notes;
          if(Array.isArray(n)) finalNotes = n; else finalNotes = [n];
      } else if (Array.isArray(noteData)) {
          finalNotes = noteData as number[];
      } else {
          finalNotes = [noteData as number];
      }

      finalNotes.forEach(n => {
          if (n > 0) play(n);
      });
    });
  }

  public async analyzeFile(file: File, cs: number, ce: number, onProg?: (p: number) => void): Promise<MelodyData> {
    const ctx = this.initCtx();
    const ab = await file.arrayBuffer();
    const audioBuf = await ctx.decodeAudioData(ab);
    const data = audioBuf.getChannelData(0);
    const sliced = data.slice(Math.floor((cs/100)*data.length), Math.floor((ce/100)*data.length));
    const freqs: number[] = [];
    const chunks = 200;
    const size = Math.floor(sliced.length / chunks);
    for (let i = 0; i < chunks; i++) {
        const window = sliced.slice(i * size, i * size + 2048);
        const pitch = this.autoCorrelate(window, audioBuf.sampleRate);
        if (pitch > 50 && pitch < 3000) freqs.push(pitch);
        if (i % 20 === 0 && onProg) onProg(Math.floor((i/chunks)*100));
    }
    if (onProg) onProg(100);
    return freqs;
  }

  private autoCorrelate(buf: Float32Array, sr: number): number {
    let rms = 0; for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
    if (Math.sqrt(rms / buf.length) < 0.01) return -1;
    let r1 = 0, r2 = buf.length - 1, thres = 0.2;
    for (let i = 0; i < buf.length / 2; i++) if (Math.abs(buf[i]) < thres) { r1 = i; break; }
    for (let i = 1; i < buf.length / 2; i++) if (Math.abs(buf[buf.length - i]) < thres) { r2 = buf.length - i; break; }
    const b = buf.slice(r1, r2); if (b.length < 2) return -1;
    const c = new Float32Array(b.length).fill(0);
    for (let i = 0; i < b.length; i++) for (let j = 0; j < b.length - i; j++) c[i] = c[i] + b[j] * b[j + i];
    let d = 0; while (d < b.length - 1 && c[d] > c[d + 1]) d++;
    
    let maxval = -1, maxpos = -1;
    for (let i = d; i < b.length; i++) {
      if (c[i] > maxval) {
        maxval = c[i];
        maxpos = i;
      }
    }
    let T0 = maxpos;
    if (T0 <= 0) return -1;
    
    // Parabolic interpolation for better precision
    let x1 = c[T0 - 1], x2 = c[T0], x3 = c[T0 + 1];
    let a = (x1 + x3 - 2 * x2) / 2;
    let bCoef = (x3 - x1) / 2;
    if (a) T0 = T0 - bCoef / (2 * a);

    return sr / T0;
  }

  public getAvailableVoices(input: string): string[] {
      // Basic implementation for Online Sequencer or MIDI text dumps if identifiable
      // For now, if it's JSON, we might check for tracks, otherwise return empty
      try {
          if (input.trim().startsWith('{') || input.trim().startsWith('[')) {
             // JSON parsing logic if needed for multi-track, but for now we assume flat or specific format
             return [];
          }
      } catch(e) {}
      
      // If regex based:
      // Online sequencer often looks like: "Instrument: Piano ... notes ..."
      // We'll return empty to indicate no specific voice filtering available unless we parse complex formats.
      return [];
  }

  public parseMelodyString(input: string, voice?: string): MelodyData {
      const trimmed = input.trim();
      if (!trimmed) return [];

      // 1. Try JSON
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) return parsed as MelodyData;
          } catch (e) { console.warn("JSON parse failed", e); }
      }

      // 2. Try Comma Separated Numbers
      const parts = trimmed.split(/[\s,]+/);
      const notes: number[] = [];
      parts.forEach(p => {
          const val = parseFloat(p);
          if (!isNaN(val) && val > 20 && val < 20000) notes.push(val);
      });
      
      if (notes.length > 0) return notes;

      return [];
  }

  public generateRandomMelody(length: number): MelodyData {
      const scale = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25]; // C Major Pentatonic
      const melody: number[] = [];
      for(let i=0; i<length; i++) {
          melody.push(scale[Math.floor(Math.random() * scale.length)]);
      }
      return melody;
  }
}

export const AUDIO = new AudioService();
